import { Mail, MessageSquare, Phone, Clock } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import ContactFormV2 from './ContactFormV2';

type InfoRow = {
  Icon: typeof Mail;
  strong: string;
  detail: string;
  href?: string;
};

const INFO_ROWS: ReadonlyArray<InfoRow> = [
  {
    Icon: Mail,
    strong: 'contact@velko-pos.com',
    detail: 'Ventes · avant-vente · presse',
    href: 'mailto:contact@velko-pos.com',
  },
  {
    Icon: MessageSquare,
    strong: '+237 656 48 88 79',
    detail: 'WhatsApp — le plus rapide',
    href: 'https://wa.me/237656488879',
  },
  {
    Icon: Phone,
    strong: '+237 656 48 88 79',
    detail: 'Lun-Ven · 8h-18h GMT+1',
    href: 'tel:+237656488879',
  },
  {
    Icon: Clock,
    strong: 'Support actif',
    detail: 'Un humain répond, pas un bot',
  },
];

export default function ContactFormSectionV2() {
  return (
    <section
      id="contact-form-section"
      className="border-y-2 border-charcoal-800 bg-white py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid gap-12 md:grid-cols-[1.1fr_0.9fr] md:gap-14">
          {/* LEFT — Form */}
          <div>
            <SectionLabel>Formulaire détaillé</SectionLabel>
            <h2 className="mt-3 font-marketing-display text-[clamp(32px,4.4vw,56px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
              Raconte-nous <span className="underline-lime">ta situation.</span>
            </h2>
            <p className="mt-4 max-w-lg font-marketing-body text-[17px] text-charcoal-600">
              Plus tu nous donnes de contexte, plus on répond vite et juste. On lit chaque message
              avant de répondre, jamais de modèle pré-rempli.
            </p>
            <div className="mt-8">
              <ContactFormV2 />
            </div>
          </div>

          {/* RIGHT — Info cards */}
          <div className="flex flex-col gap-5">
            <div className="border-2 border-charcoal-800 bg-white p-7 shadow-neobrutal-5">
              <h3 className="font-marketing-display text-[18px] font-bold tracking-[-0.01em] text-charcoal-800">
                Infos pratiques
              </h3>
              <ul className="mt-5 flex flex-col gap-3.5">
                {INFO_ROWS.map(({ Icon, strong, detail, href }) => {
                  const content = (
                    <>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-charcoal-800 bg-paper text-charcoal-800">
                        <Icon className="h-4 w-4" strokeWidth={2.2} />
                      </span>
                      <span className="flex flex-col leading-tight">
                        <span className="font-marketing-body text-[14px] font-semibold text-charcoal-800">
                          {strong}
                        </span>
                        <span className="font-marketing-body text-[13px] text-charcoal-500">
                          {detail}
                        </span>
                      </span>
                    </>
                  );
                  return (
                    <li key={strong + detail}>
                      {href ? (
                        <a
                          href={href}
                          target={href.startsWith('http') ? '_blank' : undefined}
                          rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="flex items-center gap-3 transition-colors hover:[&_.font-semibold]:text-lime-600"
                        >
                          {content}
                        </a>
                      ) : (
                        <span className="flex items-center gap-3">{content}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="border-[3px] border-charcoal-800 bg-lime-500 p-7 shadow-neobrutal-6">
              <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.1em] text-charcoal-800">
                Notre engagement
              </div>
              <div className="mt-2 font-marketing-display text-[48px] font-bold leading-none tracking-[-0.035em] text-charcoal-800">
                &lt; 24h
                <span className="ml-2 text-[20px] font-medium text-charcoal-700">
                  · tous canaux
                </span>
              </div>
              <p className="mt-3 font-marketing-body text-[14px] leading-[1.55] text-charcoal-800">
                On s'engage sur le délai de première réponse, jours ouvrables. Priorité aux
                incidents bloquants pour les clients Pro.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
